import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { toast } from "react-toastify";
import { SANCTION_TYPES } from "../../../RecipientDetail/sanctionConstants";
import { fetchSanctionProposals } from "../../../RecipientDetail/recipientHelpers";
import dataService from "../../../../../helpers/dataService";
import { getSanctionStyle } from "./DecisionModalHelpers";
import DecisionModalBanner from "./DecisionModalBanner";
import DecisionModalContext from "./DecisionModalContext";
import DecisionModalDecision from "./DecisionModalDecision";

const EpDecisionModal = ({ inscription, ep, onClose, onSaved, readOnly }) => {
  const isSuspension = inscription.type === SANCTION_TYPES.SUSPENSION;
  const [presence, setPresence] = useState(
    inscription.presenceId || (isSuspension ? "NON_CONVOQUE" : ""),
  );
  const [selectedProposalId, setSelectedProposalId] = useState(
    inscription.decisionId || inscription.selectedProposal || null,
  );
  const [radiationBRSA, setRadiationBRSA] = useState(
    inscription.radiationBRSA ?? false,
  );
  const [radiationDE, setRadiationDE] = useState(
    inscription.radiationDE ?? false,
  );
  const [note, setNote] = useState(inscription.note || "");
  const [isSaving, setIsSaving] = useState(false);

  const [proposals, setProposals] = useState([]);
  const [loadingBareme, setLoadingBareme] = useState(false);
  const [baremeError, setBaremeError] = useState(null);

  useEffect(() => {
    setLoadingBareme(true);
    setBaremeError(null);
    fetchSanctionProposals({
      userRecipient: inscription.userRecipient?.id,
      sanctionType: inscription.type,
      mainReason: inscription.motif,
      otherReasons: inscription.secondaryReasons || [],
      pregnancy: inscription.pregnancy || false,
      allProposals: true,
    })
      .then((result) => {
        const allProposals = result.proposals || [];
        setProposals(allProposals);
        setLoadingBareme(false);
      })
      .catch(() => {
        setBaremeError("Impossible de charger les propositions de sanction");
        setLoadingBareme(false);
      });
  }, [inscription]);

  const ruProposal = proposals.find(
    (p) => p.id === inscription.selectedProposal,
  );
  const alternativeProposal = proposals.find(
    (p) => p.id !== inscription.selectedProposal && p.id !== "AUCUNE_SANCTION",
  );
  const noSanctionProposal = proposals.find((p) => p.id === "AUCUNE_SANCTION");
  const isNoSanction = selectedProposalId === "AUCUNE_SANCTION";

  const isSuppression = inscription.type === SANCTION_TYPES.SUPPRESSION;
  const sanctionStyle = getSanctionStyle(isSuppression);
  const isOver62 =
    inscription.userRecipient?.age &&
    parseInt(inscription.userRecipient.age) > 62;

  const handleSave = () => {
    if (!presence) {
      toast.warning("Veuillez renseigner la présence de l'allocataire");
      return;
    }

    setIsSaving(true);

    const decisionData = {
      presence,
      sanctionId: selectedProposalId,
      radiationBRSA: isNoSanction ? false : radiationBRSA,
      radiationDE: isNoSanction ? false : radiationDE,
      note,
    };

    dataService.patch(
      `inscriptions-ep/${inscription.id}/decider-sanction-finale`,
      decisionData,
      () => {
        toast.success("Décision enregistrée avec succès");
        setIsSaving(false);
        onSaved(inscription.id);
      },
      (err) => {
        toast.error(
          "Erreur lors de l'enregistrement: " +
            (Object.values(err).join(", ") || "Erreur inconnue"),
        );
        setIsSaving(false);
      },
    );
  };

  const handleSelectProposal = (proposal) => {
    if (!proposal) return;
    setSelectedProposalId(proposal.id);
    if (proposal.id === "AUCUNE_SANCTION") {
      setRadiationBRSA(false);
      setRadiationDE(false);
    }
  };

  return ReactDOM.createPortal(
    <div
      className="position-fixed d-flex align-items-start justify-content-center"
      style={{
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        background: "rgba(15,23,42,0.6)",
        backdropFilter: "blur(4px)",
        zIndex: 10000,
        overflowY: "auto",
        padding: "40px 20px",
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="rounded animated fadeInUp faster"
        style={{
          maxWidth: 1050,
          width: "100%",
          background: "#fff",
          boxShadow: "0 25px 50px rgba(0,0,0,0.15)",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          className="d-flex justify-content-between align-items-center px-4 py-3"
          style={{
            background: "linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)",
            color: "#fff",
          }}
        >
          <div className="d-flex align-items-center" style={{ gap: 14 }}>
            <div
              className="d-flex align-items-center justify-content-center"
              style={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                background: "rgba(255,255,255,0.15)",
              }}
            >
              <i className="fa fa-gavel" style={{ fontSize: 18 }} />
            </div>
            <div>
              <h5 className="mb-0" style={{ fontSize: 17, fontWeight: 700 }}>
                {readOnly ? "Détail de la décision" : "Prise de décision"}
              </h5>
              <div style={{ fontSize: 12, opacity: 0.8 }}>
                Session Équipe Pluridisciplinaire
              </div>
            </div>
          </div>
          <button
            className="btn p-0"
            onClick={onClose}
            style={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.15)",
              color: "#fff",
              fontSize: 18,
              lineHeight: 1,
              border: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            &times;
          </button>
        </div>

        <DecisionModalBanner
          inscription={inscription}
          sanctionStyle={sanctionStyle}
          isOver62={isOver62}
        />

        {/* Body — 2 columns */}
        <div className="row no-gutters">
          <DecisionModalContext
            inscription={inscription}
            sanctionStyle={sanctionStyle}
            presence={presence}
            setPresence={setPresence}
            readOnly={readOnly}
            isSuspension={isSuspension}
          />

          <DecisionModalDecision
            inscription={inscription}
            readOnly={readOnly}
            isSuppression={isSuppression}
            selectedProposalId={selectedProposalId}
            isNoSanction={isNoSanction}
            radiationBRSA={radiationBRSA}
            setRadiationBRSA={setRadiationBRSA}
            radiationDE={radiationDE}
            setRadiationDE={setRadiationDE}
            note={note}
            setNote={setNote}
            onSelectProposal={handleSelectProposal}
            ruProposal={ruProposal}
            alternativeProposal={alternativeProposal}
            noSanctionProposal={noSanctionProposal}
            loadingBareme={loadingBareme}
            baremeError={baremeError}
          />
        </div>

        {/* Footer */}
        <div
          className="d-flex justify-content-between align-items-center px-4 py-3"
          style={{ borderTop: "1px solid #e2e8f0", background: "#f8fafc" }}
        >
          <button
            className="btn"
            onClick={onClose}
            style={{
              background: "#fff",
              border: "1px solid #e2e8f0",
              color: "#475569",
              fontSize: 14,
              fontWeight: 600,
              borderRadius: 8,
              padding: "8px 24px",
            }}
          >
            {readOnly ? "Fermer" : "Annuler"}
          </button>
          {!readOnly && (
            <button
              className="btn"
              onClick={handleSave}
              disabled={isSaving || !presence || !selectedProposalId}
              style={{
                background:
                  isSaving || !presence || !selectedProposalId
                    ? "#e2e8f0"
                    : "linear-gradient(135deg, #22c55e, #16a34a)",
                color:
                  isSaving || !presence || !selectedProposalId
                    ? "#94a3b8"
                    : "#fff",
                border: "none",
                fontSize: 15,
                fontWeight: 700,
                borderRadius: 10,
                padding: "10px 32px",
                boxShadow:
                  isSaving || !presence || !selectedProposalId
                    ? "none"
                    : "0 4px 14px rgba(34,197,94,0.3)",
                transition: "all 0.2s ease",
              }}
            >
              {isSaving ? (
                <>
                  <i className="fa fa-spinner fa-spin mr-2" />
                  Enregistrement...
                </>
              ) : (
                <>
                  <i className="fa fa-check mr-2" />
                  Enregistrer la décision
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default EpDecisionModal;
